import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Check, Lock, Play, LogOut, Flame } from "lucide-react";
import Logo from "../components/Logo";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/program").then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream">
        <Leaf className="w-8 h-8 text-clay animate-pulse" />
        <p className="mt-4 text-sm text-forest/60 tracking-[0.2em] uppercase">Cargando tu programa…</p>
      </div>
    );
  }

  const completed = data.completedDays || [];
  const total = data.totalDays;
  const pct = Math.round((completed.length / total) * 100);
  const currentDay = (() => {
    for (let d = 1; d <= total; d++) if (!completed.includes(d)) return d;
    return total;
  })();

  const isUnlocked = (day) => completed.includes(day) || day <= currentDay;

  const weeks = [1, 2, 3, 4].map((w) => ({
    n: w,
    days: data.days.filter((d) => d.week === w),
    theme: data.days.find((d) => d.week === w)?.weekTheme,
    focus: data.days.find((d) => d.week === w)?.weekFocus,
  }));

  return (
    <div className="min-h-screen bg-cream">
      <nav className="sticky top-0 z-40 backdrop-blur-xl bg-cream/80 border-b border-forest/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-forest/60">Hola, {user?.name}</span>
            <button data-testid="logout-button" onClick={() => { logout(); navigate("/"); }} className="inline-flex items-center gap-2 text-sm text-forest/70 hover:text-clay transition-colors">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {/* Progress header */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-forest text-cream rounded-3xl p-8 sm:p-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <span className="text-xs tracking-[0.2em] uppercase text-clay font-semibold">Tu progreso</span>
              <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-cream leading-tight">Bienvenida de nuevo, {user?.name}</h1>
              <p className="mt-3 text-cream/70">Continúa tu ritual. Cada día cuenta.</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="flex items-center gap-1.5 justify-center text-clay"><Flame className="w-4 h-4" /><span className="font-serif text-3xl text-cream">{completed.length}</span></div>
                <p className="text-xs text-cream/60 mt-1">días hechos</p>
              </div>
              <div className="text-center">
                <span className="font-serif text-3xl text-cream">{total - completed.length}</span>
                <p className="text-xs text-cream/60 mt-1">restantes</p>
              </div>
            </div>
          </div>
          <div className="mt-8">
            <div className="flex justify-between text-xs text-cream/60 mb-2">
              <span>{pct}% completado</span><span>Día {currentDay} de {total}</span>
            </div>
            <div className="h-2 rounded-full bg-cream/15 overflow-hidden" data-testid="global-progress">
              <div className="h-full bg-clay transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <Link to={`/programa/dia/${currentDay}`} data-testid="continue-button" className="mt-8 inline-flex items-center gap-2 rounded-full bg-clay text-cream font-medium px-6 py-3.5 hover:bg-clay-dark transition-colors">
            <Play className="w-4 h-4" /> {completed.length === 0 ? "Empezar día 1" : `Continuar día ${currentDay}`}
          </Link>
        </motion.div>

        {/* Weeks */}
        <div className="mt-12 space-y-10">
          {weeks.map((w) => (
            <section key={w.n} data-testid={`week-section-${w.n}`}>
              <div className="flex items-baseline gap-3">
                <span className="font-serif text-3xl text-clay">Semana {w.n}</span>
                <span className="text-forest/70">· {w.theme}</span>
              </div>
              <p className="text-sm text-forest/50 mt-1">{w.focus}</p>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                {w.days.map((d) => {
                  const done = completed.includes(d.day);
                  const unlocked = isUnlocked(d.day);
                  const current = d.day === currentDay;
                  const Card = unlocked ? Link : "div";
                  return (
                    <Card
                      key={d.day}
                      to={unlocked ? `/programa/dia/${d.day}` : undefined}
                      data-testid={`day-card-${d.day}`}
                      className={`group relative rounded-2xl border p-4 min-h-[112px] flex flex-col justify-between transition-all
                        ${done ? "bg-sage border-clay/30" : unlocked ? "bg-white border-forest/10 hover:-translate-y-1 hover:border-clay/40" : "bg-white/50 border-forest/5 cursor-not-allowed"}
                        ${current && !done ? "ring-2 ring-clay" : ""}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-xs tracking-[0.15em] uppercase ${unlocked ? "text-forest/50" : "text-forest/30"}`}>Día {d.day}</span>
                        {done ? <span className="w-6 h-6 rounded-full bg-clay flex items-center justify-center"><Check className="w-3.5 h-3.5 text-cream" /></span>
                          : !unlocked ? <Lock className="w-4 h-4 text-forest/30" />
                          : <span className="w-6 h-6 rounded-full bg-sage flex items-center justify-center"><Play className="w-3 h-3 text-clay" /></span>}
                      </div>
                      <p className={`font-serif text-lg leading-tight ${unlocked ? "text-forest" : "text-forest/40"}`}>{d.title}</p>
                    </Card>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
